import { Popup, Typography } from '@tunasong/ui-lib'
import { formatNumber } from '@tunasong/models'
import { useDragMove } from '@tunasong/ui-lib'
import cn from 'classnames'
import { useCallback, useState } from 'react'
import type { FC, HTMLAttributes, Ref } from 'react'
import { getPercentageDB } from '../../lib/index.js'
import styles from './channel-volume.module.css'

// Interface is defined at the top of the file

const VolumeContainer = ({ ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) => (
  <div ref={ref} {...props} className={cn(styles.container, props.className)} style={props.style} />
)

const VolumeBar = ({ ref: _ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) => (
  <div {...props} className={cn(styles.bar, props.className)} style={props.style} />
)

interface VolumeLevelProps extends HTMLAttributes<HTMLDivElement> {
  volumeLevel: number
}

const VolumeLevel = ({ volumeLevel, ...props }: VolumeLevelProps) => (
  <div
    {...props}
    className={cn(styles.level, props.className)}
    style={{ width: `${Math.min(volumeLevel * 100, 100)}%`, ...props.style }}
  />
)

export interface ChannelVolumeProps {
  className?: string
  volume: number
  onChangeVolume(volume: number): void
}

export const ChannelVolume: FC<ChannelVolumeProps> = props => {
  const { className, volume, onChangeVolume } = props

  const [ref, setRef] = useState<HTMLDivElement | null>(null)

  /** Dragging the volume bar? */

  const handleVolume = useCallback(
    (ev: MouseEvent) => {
      if (!ref) {
        return
      }
      ev.preventDefault()
      const box = ref.getBoundingClientRect()
      const percentage = Math.max(Math.min(((ev.clientX - box.left) / box.width) * 100, 100), 0)
      onChangeVolume(percentage / 100)
    },
    [onChangeVolume, ref]
  )
  const [showDB, setShowDB] = useState(false)
  const handleStart = useCallback(
    (ev: MouseEvent) => {
      handleVolume(ev)
      setShowDB(true)
    },
    [handleVolume]
  )

  const handleStop = useCallback(
    (ev: MouseEvent) => {
      handleVolume(ev)
      setShowDB(false)
    },
    [handleVolume]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  useDragMove({
    el: ref,
    onMove: handleVolume,
    onStart: handleStart,
    onStop: handleStop,
  })

  const db = `${formatNumber(getPercentageDB({ value: volume * 100 }), { digits: 1 })}dB`

  return (
    <VolumeContainer className={className} title={db} ref={setAnchorEl}>
      <VolumeBar ref={setRef}>
        <VolumeLevel volumeLevel={volume} />
      </VolumeBar>
      <Popup open={showDB} anchorEl={anchorEl} placement="top">
        <Typography variant="caption">{db}</Typography>
      </Popup>
    </VolumeContainer>
  )
}

export default ChannelVolume
